import {useCallback, useMemo, useRef} from 'react';
import {InventoryReportViewVO} from '../../../types/model/valueObject/InventoryReportViewVO';
import {useProgressiveExpandedItemsById} from '../../render/useProgressiveExpandedItemsById';

interface UseSupplierCurrentInventoryExpansionParams {
  currentInventories?: InventoryReportViewVO[];
}

interface UseSupplierCurrentInventoryExpansion {
  expandByIndex: boolean[];
  handleToggleCollapse: (index: number) => void;
  expandedItems: Record<string, true>;
  setExpandedItems: (items: object) => void;
  setExpandedAll: () => void;
  handleExpandAll: () => void;
}

/**
 * Isolated expansion state for the Supplier Current Inventory report.
 * Stores expansion by inventory id so the visible expanded state can be derived
 * without an effect-driven initialization render whenever fresh inventory data arrives.
 */
const useSupplierCurrentInventoryExpansion = ({
  currentInventories,
}: UseSupplierCurrentInventoryExpansionParams): UseSupplierCurrentInventoryExpansion => {
  const visibleInventoryIds = useMemo(
    () => currentInventories?.map((inventory) => inventory.id) ?? [],
    [currentInventories]
  );

  // Refs keep callbacks stable while always reading the latest derived values.
  const currentInventoriesRef = useRef(currentInventories);
  currentInventoriesRef.current = currentInventories;

  const {
    expandedItems,
    toggleExpandedItem,
    expandAllItems,
    collapseAllItems,
  } = useProgressiveExpandedItemsById({
    itemIds: visibleInventoryIds,
  });

  const expandByIndex = useMemo(
    () => currentInventories?.map((inventory) => Boolean(expandedItems[inventory.id])) ?? [],
    [currentInventories, expandedItems]
  );

  const handleToggleCollapse = useCallback((index: number) => {
    const inventory = currentInventoriesRef.current?.[index];

    if (!inventory) {
      return;
    }

    toggleExpandedItem(inventory.id);
  }, [toggleExpandedItem]);

  const setExpandedItems = useCallback((_items: object) => {
    // ReportDataSummary still calls this prop before setExpandedAll(false).
    // Keep it as a no-op so the existing API remains compatible.
  }, []);

  return {
    expandByIndex,
    handleToggleCollapse,
    handleExpandAll: expandAllItems,
    setExpandedAll: collapseAllItems,
    expandedItems,
    setExpandedItems,
  };
};

export { useSupplierCurrentInventoryExpansion };

